"use client";

import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Checkbox,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";

type MedicalQuestion = {
  id: number;
  question: string;
  checked: boolean;
  answer: boolean;
  remarks: string;
};

interface MedicalHistoryTableProps {
  formData: any;
  handleMedicalHistoryChange: (
    updatedMedicalHistory: MedicalQuestion[]
  ) => void;
}

const MedicalHistoryTable: React.FC<MedicalHistoryTableProps> = ({
  formData,
  handleMedicalHistoryChange,
}) => {
  const [data, setData] = React.useState<MedicalQuestion[]>([]);
  const [isInitialized, setIsInitialized] = React.useState(false);

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/medical-questions/getAllMedicalQuestions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("API Response:", JSON.stringify(response?.data, null, 2));
      if (response?.data?.isSuccess) {
        const fetchedData = response?.data?.data.map(
          (item: any, index: number) => {
            const defaultAns = item.defaultAns === true; // Ensure boolean
            return {
              id: item.id || index + 1, // Unique ID fallback
              question: item.question || "Unknown Question",
              checked: true, // Set checked based on defaultAns
              answer: defaultAns, // Align answer with defaultAns
              remarks: item.remarks || "",
            };
          }
        );
        console.log("Mapped API Data:", JSON.stringify(fetchedData, null, 2));
        setData(fetchedData);
        handleMedicalHistoryChange(fetchedData);
      } else {
        console.error("API Error:", response?.data?.message);
        toast.error(
          response?.data?.message || "Failed to fetch medical questions"
        );
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching medical questions");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!data.length || isInitialized) return;

    console.log(
      "FormData Medical History:",
      JSON.stringify(formData.medicalHistory, null, 2)
    );
    const newData = data.map((item) => {
      const matchingItem = formData.medicalHistory?.find(
        (q: any) => q.question === item.question
      );
      // Prioritize API's checked state unless formData explicitly overrides
      return {
        ...item,
        checked:
          matchingItem && matchingItem.checked !== undefined
            ? matchingItem.checked
            : item.checked,
        answer:
          matchingItem && matchingItem.answer !== undefined
            ? matchingItem.answer
            : item.answer,
        remarks:
          matchingItem && matchingItem.remarks !== undefined
            ? matchingItem.remarks || ""
            : item.remarks,
      };
    });

    console.log(
      "Initialized Data with FormData:",
      JSON.stringify(newData, null, 2)
    );
    setData(newData);
    setIsInitialized(true);
    handleMedicalHistoryChange(newData);
  }, [data, formData.medicalHistory, isInitialized]);

  const handleChangeMedical = (
    id: number,
    field: keyof MedicalQuestion,
    value: any
  ) => {
    console.log(
      `handleChangeMedical - id: ${id}, field: ${field}, value:`,
      value
    );
    console.log("Data Before Update:", JSON.stringify(data, null, 2));
    const updated = data.map((row) => {
      if (row.id === id) {
        return {
          ...row,
          [field]: field === "answer" ? value === "Yes" : value,
        };
      }
      return row;
    });
    console.log("Data After Update:", JSON.stringify(updated, null, 2));
    setData(updated);
    handleMedicalHistoryChange(updated);
  };

  return (
    <Paper
      sx={{
        padding: 0,
        margin: 0,
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.75,
          backgroundColor: "#f5f5fb",
          borderBottom: "2px solid #e0e0e0",
          fontSize: "11px",
          fontWeight: 600,
          color: "#333",
        }}
      >
        Medical History
      </Box>
      {data.length === 0 ? (
        <Box sx={{ p: 1, fontSize: "11px" }}>Loading...</Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          {data.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.4,
                minHeight: 32,
                backgroundColor:
                  Math.floor(index / 2) % 2 === 0 ? "#ffffff" : "#fafafa",
                borderBottom: "1px solid #eee",
                borderRight: {
                  md: index % 2 === 0 ? "1px solid #eee" : "none",
                },
                "&:hover": { backgroundColor: "#f0f7ff" },
              }}
            >
              <Checkbox
                checked={row.checked}
                onChange={(e) => {
                  handleChangeMedical(row.id, "checked", e.target.checked);
                }}
                sx={{
                  padding: 0,
                  color: "#6b7280",
                  "&.Mui-checked": {
                    color: "#1976d2",
                  },
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "11px",
                  fontWeight: row.checked ? 500 : 400,
                  lineHeight: 1.3,
                }}
              >
                {row.question}
              </Typography>
              <Select
                name={`select-${row.id}`}
                value={row.answer ? "Yes" : "No"}
                onChange={(e) => {
                  handleChangeMedical(row.id, "answer", e.target.value);
                }}
                size="small"
                displayEmpty
                disabled={!row.checked}
                sx={{
                  minWidth: 72,
                  padding: 0,
                  margin: 0,
                  height: "24px",
                  fontSize: "11px",
                  backgroundColor: "#f9fafb",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default MedicalHistoryTable;
